/* <case-lightbox>: tap-to-enlarge for the step photos of a workshop case
   article (sections/main-article-case.liquid). The photos come from the
   article body, so the element finds them in the article content, wraps each
   in a button and opens the native <dialog> with the large version, its
   caption and a counter. Arrow keys and a horizontal swipe step through all
   photos of the article; Escape, the close button or a tap on the dark ground
   closes it. Without JavaScript the photos simply stay as they are. */

(() => {
  if (customElements.get('case-lightbox')) return;

  /* The largest candidate of a srcset, else the plain src. */
  const largestSource = (img) => {
    const candidates = (img.getAttribute('srcset') || '')
      .split(',')
      .map((part) => part.trim().split(/\s+/))
      .filter(([url, size]) => url && /^\d+w$/.test(size || ''))
      .map(([url, size]) => ({ url, width: parseInt(size, 10) }))
      .sort((a, b) => b.width - a.width);
    return candidates.length ? candidates[0].url : img.currentSrc || img.src;
  };

  class CaseLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.caption = this.querySelector('.case-lightbox__caption');
      this.count = this.querySelector('.case-lightbox__count');
      const scope = this.closest('.case-article') || document;
      this.figures = [...scope.querySelectorAll('.case-article__content .case-pics figure')].filter((figure) =>
        figure.querySelector('img')
      );
      if (!this.dialog || !this.caption || !this.figures.length) return;

      // Created here rather than in the markup, so the closed viewer carries
      // no image without a source.
      this.image = document.createElement('img');
      this.image.className = 'case-lightbox__img';
      this.image.alt = '';
      this.image.draggable = false;
      this.caption.before(this.image);

      this.figures.forEach((figure, index) => {
        const img = figure.querySelector('img');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'case-pics__zoom';
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-label', [this.dataset.openLabel, img.alt].filter(Boolean).join(': '));
        img.replaceWith(button);
        button.append(img);
        button.addEventListener('click', () => this.open(index));
      });

      this.querySelector('.case-lightbox__close')?.addEventListener('click', () => this.close());
      this.querySelector('.case-lightbox__prev')?.addEventListener('click', () => this.step(-1));
      this.querySelector('.case-lightbox__next')?.addEventListener('click', () => this.step(1));
      this.dialog.addEventListener('click', (event) => {
        // A swipe that starts and ends on the dark ground is not a tap.
        if (this.swiped) {
          this.swiped = false;
          return;
        }
        if (event.target === this.dialog || event.target.classList.contains('case-lightbox__stage')) this.close();
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('case-lightbox-open');
        this.figures[this.index]?.querySelector('.case-pics__zoom')?.focus({ preventScroll: true });
      });

      let startX = null;
      let startY = null;
      this.dialog.addEventListener('pointerdown', (event) => {
        startX = event.clientX;
        startY = event.clientY;
      });
      this.dialog.addEventListener('pointercancel', () => {
        startX = null;
      });
      this.dialog.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.swiped = true;
          this.step(dx < 0 ? 1 : -1);
        }
      });
    }

    open(index) {
      this.index = index;
      this.render();
      document.documentElement.classList.add('case-lightbox-open');
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
    }

    close() {
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        this.dialog.dispatchEvent(new Event('close'));
      }
    }

    step(delta) {
      const total = this.figures.length;
      this.index = (this.index + delta + total) % total;
      this.render();
    }

    render() {
      const figure = this.figures[this.index];
      const img = figure.querySelector('img');
      // The thumbnail is already loaded, so it shows at once; the large
      // version replaces it when it has arrived (slow mobile connections).
      const large = largestSource(img);
      const shown = img.currentSrc || img.src;
      this.image.src = shown || large;
      this.image.alt = img.alt;
      if (large && large !== shown) {
        const preload = new Image();
        preload.onload = () => {
          if (this.figures[this.index] === figure) this.image.src = large;
        };
        preload.src = large;
      }
      const caption = figure.querySelector('figcaption')?.textContent.trim() || '';
      this.caption.textContent = caption;
      this.caption.hidden = !caption;
      if (this.count) this.count.textContent = `${this.index + 1} / ${this.figures.length}`;
      this.toggleAttribute('data-single', this.figures.length < 2);
    }
  }

  customElements.define('case-lightbox', CaseLightbox);
})();
